// ============================================
// BULLET SYSTEM - 弾丸の更新
// ============================================

import { getP5 } from '../../core/p5-instance';
import { CONFIG } from '../../config/game-config';
import {
    getBullets,
    getEnemyBullets,
    getEnemies,
    getPlayer,
    getPowerLevel,
    getShieldActive, setShieldActive,
    setShieldTimer
} from '../../store';
import { triggerPlayerDeath } from '../../features/player/player';
import { createHitParticles } from '../../features/particles/particles';

/**
 * updateBullets() - 自機弾の更新
 */
export function updateBullets(dt: number): void {
    const p = getP5();
    const bullets = getBullets();
    const enemies = getEnemies();
    const powerLevel = getPowerLevel();

    for (let i = bullets.length - 1; i >= 0; i--) {
        const bullet = bullets[i];

        if (bullet.isHoming) {
            // 発射遅延のカウントダウン
            if (bullet.homingDelay !== undefined && bullet.homingDelay > 0) {
                bullet.homingDelay -= dt;
                continue;  // 遅延中は処理をスキップ
            }
            // アクティブ化
            if (bullet.homingLaunchActive === false) {
                bullet.homingLaunchActive = true;
            }

            updateHomingBullet(bullet, enemies, powerLevel, dt);
        } else {
            bullet.y -= CONFIG.bulletSpeed * dt;
        }

        bullet.trail.push({ x: bullet.x, y: bullet.y });
        const maxTrailLength = bullet.homingTrailLength || 10;
        if (bullet.trail.length > maxTrailLength) {
            bullet.trail.shift();
        }

        // 画面外判定（ホーミング弾のspread/curveフェーズは横方向で削除しない）
        const isHomingEarlyPhase = bullet.isHoming &&
            (bullet.homingPhase === 'spread' || bullet.homingPhase === 'curve');
        const outOfBoundsY = bullet.y < -20 || bullet.y > p.height + 20;
        const gameAreaX = CONFIG.layout.gameAreaX;
        const outOfBoundsX = bullet.x < gameAreaX - 50 || bullet.x > gameAreaX + CONFIG.width + 50;

        if (outOfBoundsY || (outOfBoundsX && !isHomingEarlyPhase)) {
            bullets.splice(i, 1);
        }
    }
}

/**
 * ホーミング弾の更新処理（3フェーズシステム）
 * - spread: 拡散フェーズ - 初期角度で直進
 * - curve: 曲がりフェーズ - ターゲット方向へ緩やかに旋回
 * - homing: 追尾フェーズ - 強いターン率で追尾
 */
function updateHomingBullet(
    bullet: ReturnType<typeof getBullets>[number],
    enemies: ReturnType<typeof getEnemies>,
    powerLevel: number,
    dt: number
): void {
    const p = getP5();
    const homingCfg = CONFIG.ships.hunter.homingByPower;
    const lvIndex = p.constrain(powerLevel - 1, 0, 3);

    // 経過フレームをトラッキング（加速用）
    if (bullet.homingAge === undefined) {
        bullet.homingAge = 0;
    }
    bullet.homingAge += dt;

    // 加速計算: 初速から最高速へ徐々に加速
    const accelDuration = homingCfg.accelerationDuration;
    const initialMult = homingCfg.initialSpeedMult;
    const accelProgress = p.constrain(bullet.homingAge / accelDuration, 0, 1);
    const speedMult = p.lerp(initialMult, 1.0, accelProgress);

    // ターゲット検索（全フェーズで実行）
    if (!bullet.target || !enemies.includes(bullet.target) || bullet.target.hp <= 0) {
        let closestDist = Infinity;
        let closestEnemy = null;
        for (const enemy of enemies) {
            const d = p.dist(bullet.x, bullet.y, enemy.x, enemy.y);
            if (d < closestDist) {
                closestDist = d;
                closestEnemy = enemy;
            }
        }
        bullet.target = closestEnemy;
    }

    // フェーズタイマー減算
    if (bullet.homingPhaseTimer !== undefined) {
        bullet.homingPhaseTimer -= dt;
    }

    const phase = bullet.homingPhase || 'homing';
    const baseSpeed = (bullet.homingSpeed || CONFIG.ships.hunter.homingBulletSpeed) * speedMult;

    if (phase === 'spread') {
        // spreadフェーズ: 初期角度で直進（旋回なし）
        // 加速を適用（方向は維持、速度のみ変更）
        let currentAngle = p.atan2(bullet.vy, bullet.vx);
        const currentSpeed = p.sqrt(bullet.vx * bullet.vx + bullet.vy * bullet.vy);
        const targetSpeed = (bullet.homingSpeed || CONFIG.ships.hunter.homingBulletSpeed) * speedMult;

        // 画面外に出そうな場合、中心に向かって旋回
        const margin = 10;
        const gameAreaX = CONFIG.layout.gameAreaX;
        const centerX = gameAreaX + CONFIG.width / 2;
        if (bullet.x < gameAreaX + margin || bullet.x > gameAreaX + CONFIG.width - margin) {
            // 画面中央上方向への角度
            const returnAngle = p.atan2(-100, centerX - bullet.x);
            let angleDiff = returnAngle - currentAngle;
            // 角度差を正規化
            while (angleDiff > p.PI) {
                angleDiff -= p.TWO_PI;
            }
            while (angleDiff < -p.PI) {
                angleDiff += p.TWO_PI;
            }
            // 強めに旋回（画面外からの復帰）
            const maxTurn = 0.15 * dt;
            currentAngle += p.constrain(angleDiff, -maxTurn, maxTurn);
        }

        // 初期速度から加速（方向を維持または調整後）
        if (currentSpeed > 0) {
            bullet.vx = p.cos(currentAngle) * targetSpeed;
            bullet.vy = p.sin(currentAngle) * targetSpeed;
        }

        // フェーズ終了判定
        if (bullet.homingPhaseTimer !== undefined && bullet.homingPhaseTimer <= 0) {
            bullet.homingPhase = 'curve';
            // 性格に応じたフェーズ時間倍率を適用
            const phaseMult = bullet.missilePersonality
                ? homingCfg.personality[bullet.missilePersonality].phaseDurationMult
                : 1.0;
            bullet.homingPhaseTimer = homingCfg.curveDuration[lvIndex] * phaseMult;
        }
    } else if (phase === 'curve') {
        // curveフェーズ: ターゲット方向へ緩やかに旋回（定率制御）
        if (bullet.target) {
            // 天才ミサイルは敵の移動先を予測
            const targetX = bullet.target.x;
            let targetY = bullet.target.y;
            if (bullet.missilePersonality === 'tensai') {
                const predictFrames = homingCfg.personality.tensai.predictFrames;
                // 敵の速度から予測位置を計算
                targetY += bullet.target.speedMult * CONFIG.enemyBaseSpeed * predictFrames;
            }
            const dx = targetX - bullet.x;
            const dy = targetY - bullet.y;

            const targetAngle = p.atan2(dy, dx);
            let currentAngle = p.atan2(bullet.vy, bullet.vx);
            let angleDiff = targetAngle - currentAngle;

            // 角度差を -PI ~ PI に正規化
            while (angleDiff > p.PI) {
                angleDiff -= p.TWO_PI;
            }
            while (angleDiff < -p.PI) {
                angleDiff += p.TWO_PI;
            }

            // 定率制御（一定量だけ旋回）
            const maxTurn = homingCfg.curveTurnRate * dt;
            currentAngle += p.constrain(angleDiff, -maxTurn, maxTurn);

            // 速度: curveSpeed → bulletSpeed へ lerp
            const totalCurveDuration = homingCfg.curveDuration[lvIndex];
            const progress = bullet.homingPhaseTimer !== undefined
                ? 1 - (bullet.homingPhaseTimer / totalCurveDuration)
                : 1;
            const speed = p.lerp(homingCfg.curveSpeed, baseSpeed, progress);

            bullet.vx = p.cos(currentAngle) * speed;
            bullet.vy = p.sin(currentAngle) * speed;
        }

        // フェーズ終了判定
        if (bullet.homingPhaseTimer !== undefined && bullet.homingPhaseTimer <= 0) {
            bullet.homingPhase = 'homing';
        }
    } else {
        // homingフェーズ: より強いターン率で追尾（定率制御）
        if (bullet.target) {
            // 天才ミサイルは敵の移動先を予測
            const targetX = bullet.target.x;
            let targetY = bullet.target.y;
            if (bullet.missilePersonality === 'tensai') {
                const predictFrames = homingCfg.personality.tensai.predictFrames;
                // 敵の速度から予測位置を計算
                targetY += bullet.target.speedMult * CONFIG.enemyBaseSpeed * predictFrames;
            }
            const dx = targetX - bullet.x;
            const dy = targetY - bullet.y;
            const distToTarget = p.sqrt(dx * dx + dy * dy);

            // 速度: bulletSpeed + 距離加速（既存ロジック維持）
            const speed = baseSpeed + (distToTarget < 100 ? CONFIG.ships.hunter.homingAccel * (100 - distToTarget) / 10 : 0);

            const targetAngle = p.atan2(dy, dx);
            let currentAngle = p.atan2(bullet.vy, bullet.vx);
            let angleDiff = targetAngle - currentAngle;

            // 角度差を -PI ~ PI に正規化
            while (angleDiff > p.PI) {
                angleDiff -= p.TWO_PI;
            }
            while (angleDiff < -p.PI) {
                angleDiff += p.TWO_PI;
            }

            // ターン率: turnRate * homingTurnRateMultiplier（定率制御）
            const baseTurnRate = homingCfg.turnRate[lvIndex] * homingCfg.homingTurnRateMultiplier;
            let maxTurn = baseTurnRate * dt;
            // 近距離でターン率増加
            if (distToTarget < 80) {
                maxTurn = p.min(1.0, maxTurn * 2);
            }

            currentAngle += p.constrain(angleDiff, -maxTurn, maxTurn);
            bullet.vx = p.cos(currentAngle) * speed;
            bullet.vy = p.sin(currentAngle) * speed;
        }
    }

    bullet.x += bullet.vx * dt;
    bullet.y += bullet.vy * dt;
}

/**
 * updateEnemyBulletsMovementOnly() - 敵弾の移動のみ（衝突判定なし）
 * 死亡/待機中に使用。弾は移動し画面外で消去されるが、プレイヤーへの当たり判定は行わない。
 * 追尾弾は追尾を無効化し、現在の速度で直進する。
 */
export function updateEnemyBulletsMovementOnly(dt: number): void {
    const p = getP5();
    const enemyBullets = getEnemyBullets();

    for (let i = enemyBullets.length - 1; i >= 0; i--) {
        const bullet = enemyBullets[i];
        bullet.x += bullet.vx * dt;
        bullet.y += bullet.vy * dt;

        const gameAreaX = CONFIG.layout.gameAreaX;
        if (bullet.y > p.height + 20 || bullet.y < -20 ||
            bullet.x < gameAreaX - 20 || bullet.x > gameAreaX + CONFIG.width + 20) {
            enemyBullets.splice(i, 1);
        }
    }
}

/**
 * updateEnemyBullets() - 敵弾の更新
 */
export function updateEnemyBullets(dt: number): void {
    const p = getP5();
    const enemyBullets = getEnemyBullets();
    const player = getPlayer();
    if (!player) {
        return;
    }

    for (let i = enemyBullets.length - 1; i >= 0; i--) {
        const bullet = enemyBullets[i];
        if (bullet.homing) {
            const angle = p.atan2(player.y - bullet.y, player.x - bullet.x);
            const homingLerpFactor = 1 - Math.pow(1 - 0.008, dt);
            bullet.vx = p.lerp(bullet.vx, p.cos(angle) * bullet.speed, homingLerpFactor);
            bullet.vy = p.lerp(bullet.vy, p.sin(angle) * bullet.speed, homingLerpFactor);
            bullet.homingLife = (bullet.homingLife || 180) - dt;
            if (bullet.homingLife <= 0) {
                bullet.homing = false;
            }
        }
        bullet.x += bullet.vx * dt;
        bullet.y += bullet.vy * dt;

        const gameAreaX = CONFIG.layout.gameAreaX;
        if (bullet.y > p.height + 20 || bullet.y < -20 || bullet.x < gameAreaX - 20 || bullet.x > gameAreaX + CONFIG.width + 20) {
            enemyBullets.splice(i, 1);
            continue;
        }

        if (p.dist(bullet.x, bullet.y, player.x, player.y) < player.size / 2 + bullet.size / 2 && player.state?.name === 'Normal') {
            // 無敵時間中は弾を消すだけ
            if (player.invincibleTimer && player.invincibleTimer > 0) {
                enemyBullets.splice(i, 1);
                continue;
            }
            if (getShieldActive()) {
                createHitParticles(bullet.x, bullet.y);
                enemyBullets.splice(i, 1);
                setShieldActive(false);
                setShieldTimer(0);
            } else {
                triggerPlayerDeath();
            }
        }
    }
}
